import * as React from 'react';
const { useCallback } = React;
import { bindActionCreators } from 'redux';
import { getPropInSafe } from '@seeneco-ui/core/utils';

import { withAutoUpdate, IBusinessState } from '../../../../flux';
import { integrationsActionsPack } from '../../actions';
import { integrationsSelectorsPack } from '../../selectors';
import {
	openUpdateExternalSystemAccountFormDrawer,
	openRemoveExternalSystemAccountFormDrawer,
} from '../forms';
import XIntegrationsEntry from './entry.view';


type IntegrationsEntryProps = {
	isFetching?: boolean;
	cmsMap?: Record<string, CashManagementSystem>;
	accountsList?: Array<ExternalSystemAccount>;
	ssiMap?: Record<string, SubsystemInstance>;
	resMap?: Record<string, RemoteExternalService>;
	fetchCashManagementSystems?: () => void;
	fetchExternalSystemAccounts?: () => void;
	fetchTenantIntegrationsData?: () => void;
	removeExternalSystemAccount?: (ID: number) => void;
};

const IntegrationsEntry: React.SFC<IntegrationsEntryProps> = (props) => {
	const {
		isFetching,
		cmsMap,
		accountsList,
		ssiMap,
		resMap,
		fetchCashManagementSystems,
		fetchExternalSystemAccounts,
		fetchTenantIntegrationsData,
		...rest
	} = props;

	const handleAddAccount = useCallback((cmsGUID: string) => {
		openUpdateExternalSystemAccountFormDrawer({
			type: 'ADD',
			cms: cmsMap[cmsGUID],
		});
	}, [cmsMap]);
	const handleChangeAccount = useCallback((account: ExternalSystemAccount) => {
		const cmsGUID = getPropInSafe(ssiMap, o => o[account.ExternalSSInstanceID].GUID, '');

		openUpdateExternalSystemAccountFormDrawer({
			type: 'EDIT',
			accountID: account.ID,
			cms: cmsMap[cmsGUID],
		});
	}, [cmsMap, ssiMap]);
	const handleRemoveAccount = useCallback((account: ExternalSystemAccount) => {
		openRemoveExternalSystemAccountFormDrawer({
			account,
			onRemove: props.removeExternalSystemAccount,
		});
	}, []);

	return (
		<XIntegrationsEntry
			{...rest}
			isFetching={isFetching}
			cmsMap={cmsMap}
			accountsList={accountsList}
			ssiMap={ssiMap}
			resMap={resMap}
			onAddAccount={handleAddAccount}
			onChangeAccount={handleChangeAccount}
			onRemoveAccount={handleRemoveAccount}
		/>
	);
}

const selectIsFetching = state => integrationsSelectorsPack.selectAsyncCashManagementSystems.selectIsFetching(state)
	|| integrationsSelectorsPack.selectAsyncExternalSubsystemInstances.selectIsFetching(state)
	|| integrationsSelectorsPack.selectAsyncExternalSystemAccounts.selectIsFetching(state)
	|| integrationsSelectorsPack.selectAsyncRemoteExternalServices.selectIsFetching(state);

function mapStateToProps(state: IBusinessState): Partial<IntegrationsEntryProps> {
	return {
		isFetching: selectIsFetching(state),
		cmsMap: integrationsSelectorsPack.selectCMSByGUIDMap(state),
		accountsList: integrationsSelectorsPack.selectExternalSystemAccountsList(state),
		ssiMap: integrationsSelectorsPack.selectExternalSubSystemInstancesByIDMap(state),
		resMap: integrationsSelectorsPack.selectAsyncRemoteExternalServices.selectItem(state),
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({
		fetchCashManagementSystems: integrationsActionsPack.actions.fetchCashManagementSystems,
		fetchExternalSystemAccounts: integrationsActionsPack.actions.fetchExternalSystemAccounts,
		fetchTenantIntegrationsData: integrationsActionsPack.actions.fetchTenantIntegrationsData,
		removeExternalSystemAccount: integrationsActionsPack.actions.removeExternalSystemAccount,
	}, dispatch);
}

export default withAutoUpdate<IBusinessState, IntegrationsEntryProps>(mapStateToProps, mapDispatchToProps)({
	cmsDidInvalidate: [
		integrationsSelectorsPack.selectAsyncCashManagementSystems.selectDidInvalidate,
		props => props.fetchCashManagementSystems()
	],
	esaDidInvalidate: [
		integrationsSelectorsPack.selectAsyncExternalSystemAccounts.selectDidInvalidate,
		props => props.fetchExternalSystemAccounts()
	],
	integrationsDataDidInvalidate: [
		integrationsSelectorsPack.selectAsyncExternalSubsystemInstances.selectDidInvalidate,
		props => props.fetchTenantIntegrationsData()
	]
})(IntegrationsEntry);
